<template>
  <div class="ue-expenses-edit">
    <el-card>
      <div class="header">
        <h2>{{ pageTitle }} (Год: {{ formData.academicYear }})</h2>
        <div>
          <el-button @click="goBack">Назад</el-button>
          <el-button type="primary" @click="saveData" :loading="saving" v-if="formData.status !== 'APPROVED' && (hasAccess(adminRoles) || hasAccess(managerRoles) || hasAccess(universityManagerRoles))">Сохранить</el-button>
        </div>
      </div>

      <div class="table-container" @paste="handlePaste" @keydown="handleKeydown">
        <table class="grid-table">
          <thead>
            <tr>
              <th>Категория должностей</th>
              <th>Штатное кол-во работников (единиц)</th>
              <th>Факт-е кол-во работников (единиц)</th>
              <th>{{ expenseType === 'OSMS' ? 'Объект исчисления отчислений' : ((expenseType === 'SOCIAL_TAX' || expenseType === 'SOCIAL_DEDUCTION') ? 'Налогооблагаемый фонд оплаты труда' : 'Ежемесячный доход / Фонд оплаты') }}</th>
              <th>{{ expenseType === 'OPVR' ? 'Размер обязательного пенсионного взноса работодателя (%)' : (expenseType === 'OSMS' ? 'Размер отчислений (%)' : (expenseType === 'SOCIAL_TAX' ? 'Ставка социального налога (%)' : (expenseType === 'SOCIAL_DEDUCTION' ? 'Ставка социальных отчислений (%)' : 'Ставка (%)'))) }}</th>
              <th>{{ expenseType === 'OPVR' ? 'Расчетная сумма взноса' : (expenseType === 'OSMS' ? 'Расчетная сумма отчислений на обязательное социальное медицинское страхование' : (expenseType === 'SOCIAL_TAX' ? 'Расчетная сумма налога' : (expenseType === 'SOCIAL_DEDUCTION' ? 'Расчетная сумма социальных отчислений' : 'Расчетная сумма'))) }}</th>
              <th>{{ expenseType === 'OPVR' ? 'Фактическая сумма взноса' : (expenseType === 'OSMS' ? 'Фактическая сумма отчислений на обязательное социальное медицинское страхование' : (expenseType === 'SOCIAL_TAX' ? 'Фактическая сумма налога' : (expenseType === 'SOCIAL_DEDUCTION' ? 'Фактическая сумма социальных отчислений' : 'Фактическая сумма'))) }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in formData.details" :key="item.uuid || item.title" :class="{'group-row': item.group, 'calculated-row': item.group && item.title === 'ВСЕГО:'}">
              <td class="title-cell" :style="getTitleStyle(item)">
                {{ item.title }}
                <el-button type="danger" size="small"
                           @click="removeCategory(item.categoryType)"
                           v-if="item.group && item.title !== 'ВСЕГО:' && formData.status !== 'APPROVED' && (hasAccess(adminRoles) || hasAccess(managerRoles) || hasAccess(universityManagerRoles))">
                  <font-awesome-icon :icon="['fas', 'trash']" />
                </el-button>
              </td>
              
              <!-- Staff Count -->
              <td v-if="item.group && item.title !== 'ВСЕГО:'" :data-item-idx="index" data-col="0"><el-input v-model="item.staffCount" :formatter="formatInputNumber" :parser="parseInputNumber" @input="recalculate" /></td>
              <td v-else-if="item.group && item.title === 'ВСЕГО:'" class="readonly-text">{{ formatInputNumber(calculateTotalStaff()) || '0' }}</td>
              <td v-else class="readonly-text"></td>

              <!-- Fact Count -->
              <td v-if="item.group && item.title !== 'ВСЕГО:'" :data-item-idx="index" data-col="1"><el-input v-model="item.factCount" :formatter="formatInputNumber" :parser="parseInputNumber" @input="recalculate" /></td>
              <td v-else-if="item.group && item.title === 'ВСЕГО:'" class="readonly-text">{{ formatInputNumber(calculateTotalFact()) || '0' }}</td>
              <td v-else class="readonly-text"></td>

              <!-- Income -->
              <td v-if="!item.group" :data-item-idx="index" data-col="2"><el-input v-model="item.income" :formatter="formatInputNumber" :parser="parseInputNumber" @input="recalculate" /></td>
              <td v-else class="readonly-text">{{ formatNumber(item.income) }}</td>

              <!-- Rate -->
              <td v-if="item.group && item.title !== 'ВСЕГО:'" :data-item-idx="index" data-col="3"><el-input v-model="item.rate" :formatter="formatInputNumber" :parser="parseInputNumber" @input="recalculate" /></td>
              <td v-else-if="!item.group" class="readonly-text">{{ item.rate }}</td>
              <td v-else class="readonly-text"></td>

              <!-- Calculated Sum -->
              <td v-if="!item.group" class="readonly-text" style="background-color: var(--el-fill-color-light); color: var(--el-text-color-regular);">{{ formatNumber(item.calculatedSum) }}</td>
              <td v-else class="readonly-text">{{ formatNumber(item.calculatedSum) }}</td>

              <!-- Actual Sum -->
              <td v-if="!item.group" :data-item-idx="index" data-col="4"><el-input v-model="item.actualSum" :formatter="formatInputNumber" :parser="parseInputNumber" @input="recalculate" /></td>
              <td v-else class="readonly-text">{{ formatNumber(item.actualSum) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="add-category-section" v-if="formData.status !== 'APPROVED' && (hasAccess(adminRoles) || hasAccess(managerRoles) || hasAccess(universityManagerRoles)) && availableCategories.length > 0">
        <el-select v-model="selectedCategoryToAdd" placeholder="Выберите категорию" size="small" style="width: 300px; margin-right: 10px;">
          <el-option
            v-for="cat in availableCategories"
            :key="cat.id"
            :label="cat.title"
            :value="cat.id">
          </el-option>
        </el-select>
        <el-button type="success" size="small" @click="addCategory" :disabled="!selectedCategoryToAdd">
          <font-awesome-icon :icon="['fas', 'plus']"/> Добавить категорию
        </el-button>
      </div>
    </el-card>

    <vue-loading :active.sync="isLoading" :is-full-page="true"></vue-loading>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import ueExpensesService from '@/services/ueExpensesService';
import authService from '@/services/authService';
import { showErrorNotify, showSuccessNotify } from '@/utils/notificationUtils';
import VueLoading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/css/index.css';
import {handleGridKeydown} from "@/utils/gridKeyboardNav";

export default defineComponent({
  name: 'UeExpensesEditView',
  components: {
    VueLoading,
  },
  props: {
    expenseType: { type: String, required: true },
    pageTitle: { type: String, required: true }
  },
  setup(props) {
    const route = useRoute();
    const router = useRouter();
    const isLoading = ref(false);
    const saving = ref(false);
    const formData = ref<any>({ details: [] });
    const selectedCategoryToAdd = ref('');

    const ALL_CATEGORIES = [
      { id: 'ADMIN_PERSONNEL', title: 'Адм.упр.персонала' },
      { id: 'TEACHING_STAFF', title: 'Пед.персонал' },
      { id: 'SUPPORT_STAFF', title: 'Учебно вспом-й персонал' },
      { id: 'MAINTENANCE_STAFF', title: 'Обслуживающий и прочий персонал' }
    ];

    const availableCategories = computed(() => {
      if (!formData.value.details) return ALL_CATEGORIES;
      const usedTypes = new Set(formData.value.details.map((d: any) => d.categoryType).filter(Boolean));
      return ALL_CATEGORIES.filter(c => !usedTypes.has(c.id));
    });

    const adminRoles = authService.getAdminRoles();
    const managerRoles = authService.getManagerRoles();
    const universityManagerRoles = authService.getUniversityManagerRoles();

    const hasAccess = (roles: string[]) => {
      return authService.hasAccess(roles);
    };

    const loadData = async () => {
      const id = route.params.uuid as string;
      if (!id) return;
      isLoading.value = true;
      try {
        const data = await ueExpensesService.getById(id);
        
        if (data && data.details) {
          data.details.forEach((d: any) => {
            if (d.income === 0 || d.income === '0' || d.income === '0.00') d.income = '';
            if (d.rate === 0 || d.rate === '0' || d.rate === '0.00') d.rate = '';
            if (d.actualSum === 0 || d.actualSum === '0' || d.actualSum === '0.00') d.actualSum = '';
            if (d.staffCount === 0 || d.staffCount === '0' || d.staffCount === '0.00') d.staffCount = '';
            if (d.factCount === 0 || d.factCount === '0' || d.factCount === '0.00') d.factCount = '';
          });
        }
        
        formData.value = data;
        recalculate();
      } catch (error) {
        showErrorNotify('Ошибка при загрузке данных');
      } finally {
        isLoading.value = false;
      }
    };

    const goBack = () => {
      router.go(-1);
    };

    const saveData = async () => {
      saving.value = true;
      try {
        await ueExpensesService.update(formData.value);
        showSuccessNotify('Данные успешно сохранены');
        loadData();
      } catch (error) {
        showErrorNotify('Ошибка при сохранении');
      } finally {
        saving.value = false;
      }
    };

    const getTitleStyle = (item: any) => {
      if (item.title === 'ВСЕГО:') return { fontWeight: 'bold' };
      if (item.group) return { fontWeight: 'bold', paddingLeft: '10px' };
      return { paddingLeft: '30px' };
    };

    const calculateTotalStaff = () => {
      return formData.value.details
        .filter((d: any) => d.group && d.title !== 'ВСЕГО:')
        .reduce((sum: number, d: any) => sum + parseFloat(d.staffCount || 0), 0);
    };

    const calculateTotalFact = () => {
      return formData.value.details
        .filter((d: any) => d.group && d.title !== 'ВСЕГО:')
        .reduce((sum: number, d: any) => sum + parseFloat(d.factCount || 0), 0);
    };

    const recalculate = () => {
      if (!formData.value.details || formData.value.details.length === 0) return;

      // Reset group sums
      const groups = formData.value.details.filter((d: any) => d.group);
      groups.forEach((g: any) => {
        g.income = 0;
        g.calculatedSum = 0;
        g.actualSum = 0;
      });

      // Aggregate months into categories
      let currentGroup: any = null;
      for (const item of formData.value.details) {
        if (item.title === 'ВСЕГО:') continue;
        if (item.group) {
          currentGroup = item;
        } else if (currentGroup && !item.group && item.categoryType === currentGroup.categoryType) {
          item.rate = currentGroup.rate;

          const inc = parseFloat(item.income || 0);
          const r = parseFloat(item.rate || 0);
          
          // Auto calculate calculatedSum
          item.calculatedSum = (inc * r / 100).toFixed(2);
          
          currentGroup.income += inc;
          currentGroup.calculatedSum += parseFloat(item.calculatedSum || 0);
          currentGroup.actualSum += parseFloat(item.actualSum || 0);
        }
      }

      // Aggregate categories into ВСЕГО
      const root = formData.value.details.find((d: any) => d.title === 'ВСЕГО:');
      if (root) {
        root.income = 0;
        root.calculatedSum = 0;
        root.actualSum = 0;
        for (const g of groups) {
          if (g.title === 'ВСЕГО:') continue;
          root.income += g.income;
          root.calculatedSum += g.calculatedSum;
          root.actualSum += g.actualSum;
        }
      }
    };

    const addCategory = () => {
      if (!selectedCategoryToAdd.value) return;
      const catDef = ALL_CATEGORIES.find(c => c.id === selectedCategoryToAdd.value);
      if (!catDef) return;

      if (!formData.value.details) {
        formData.value.details = [];
      }
      
      // Ensure ВСЕГО exists
      let root = formData.value.details.find((d: any) => d.title === 'ВСЕГО:');
      if (!root) {
        root = { title: 'ВСЕГО:', group: true, income: 0, calculatedSum: 0, actualSum: 0 };
        formData.value.details.push(root);
      }

      // Add Category Root Row
      const newCategoryRow = {
        title: catDef.title,
        group: true,
        categoryType: catDef.id,
        staffCount: '',
        factCount: '',
        income: '',
        rate: '',
        calculatedSum: '',
        actualSum: ''
      };
      formData.value.details.push(newCategoryRow);

      const MONTH_NAMES = [
        "январь", "февраль", "март", "апрель", "май", "июнь",
        "июль", "август", "сентябрь", "октябрь", "ноябрь", "декабрь"
      ];

      // Add 12 Month Rows
      for (let i = 1; i <= 12; i++) {
        formData.value.details.push({
          title: MONTH_NAMES[i - 1],
          group: false,
          categoryType: catDef.id,
          month: i,
          income: '',
          rate: '',
          calculatedSum: '',
          actualSum: ''
        });
      }

      selectedCategoryToAdd.value = '';
      recalculate();
    };

    const removeCategory = (categoryId: string) => {
      if (!confirm('Вы уверены, что хотите удалить эту категорию? Все данные по ней будут потеряны.')) return;
      formData.value.details = formData.value.details.filter((d: any) => d.categoryType !== categoryId || d.title === 'ВСЕГО:');
      recalculate();
    };

    const formatNumber = (val: any) => {
      if (val === undefined || val === null || isNaN(val as number)) return '0,00';
      const formatted = parseFloat(val).toFixed(2);
      const parts = formatted.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
      return parts.join(',');
    };

    const formatInputNumber = (value: any) => {
      if (value === undefined || value === null || value === '') return '';
      const parts = value.toString().split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
      return parts.join(',');
    };

    const parseInputNumber = (value: any) => {
      if (!value) return '';
      let parsed = value.toString().replace(',', '.');
      parsed = parsed.replace(/[^\d.]/g, '');
      const parts = parsed.split('.');
      if (parts.length > 2) {
        parsed = parts[0] + '.' + parts.slice(1).join('');
      }
      return parsed;
    };

    const handleKeydown = (event: Event) => {
      const el = (event.currentTarget as HTMLElement);
      handleGridKeydown(event as KeyboardEvent, el);
    };

    const handlePaste = (event: ClipboardEvent) => {
      const target = event.target as HTMLElement;
      if (!target || target.tagName !== 'INPUT') return;

      const td = target.closest('td');
      if (!td) return;

      const startItemIdx = parseInt(td.dataset.itemIdx || '');
      const startCol = parseInt(td.dataset.col || '');

      if (isNaN(startItemIdx) || isNaN(startCol)) return;

      const clipboardData = event.clipboardData || (window as any).clipboardData;
      if (!clipboardData) return;

      const text = clipboardData.getData('text/plain');
      if (!text) return;

      const rows = text.split(/\r?\n/).filter((r: string) => r.trim() !== '');
      if (rows.length <= 1 && !rows[0].includes('\t')) return;

      event.preventDefault();

      const COL_FIELDS = ['staffCount', 'factCount', 'income', 'rate', 'actualSum'];

      let currentItemIdx = startItemIdx;

      for (let r = 0; r < rows.length; r++) {
        const cols = rows[r].split('\t');
        
        if (currentItemIdx >= formData.value.details.length) break;
        let currentItem = formData.value.details[currentItemIdx];
        
        while (currentItem && currentItem.title === 'ВСЕГО:') {
          currentItemIdx++;
          currentItem = formData.value.details[currentItemIdx];
        }

        if (currentItemIdx >= formData.value.details.length) break;

        for (let c = 0; c < cols.length; c++) {
          const colIdx = startCol + c;
          if (colIdx >= COL_FIELDS.length) break;

          const field = COL_FIELDS[colIdx];
          
          const isGroup = currentItem.group;
          const isEditable = 
            (isGroup && (field === 'staffCount' || field === 'factCount' || field === 'rate')) ||
            (!isGroup && (field === 'income' || field === 'actualSum'));
            
          if (isEditable) {
            const rawVal = String(cols[c]).replace(/\s/g, '').replace(',', '.');
            const num = parseFloat(rawVal);
            currentItem[field] = isNaN(num) || num === 0 ? '' : num;
          }
        }

        currentItemIdx++;
      }

      recalculate();
    };

    onMounted(() => {
      loadData();
    });

    return {
      isLoading,
      saving,
      formData,
      goBack,
      saveData,
      hasAccess,
      adminRoles,
      managerRoles,
      universityManagerRoles,
      getTitleStyle,
      recalculate,
      calculateTotalStaff,
      calculateTotalFact,
      availableCategories,
      selectedCategoryToAdd,
      addCategory,
      removeCategory,
      formatNumber,
      formatInputNumber,
      parseInputNumber,
      handleKeydown,
      handlePaste
    };
  }
});
</script>

<style scoped>
.ue-expenses-edit { }
.header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; }
.table-container { width: 100%; overflow-x: auto; }
.grid-table { width: 100%; border-collapse: collapse; min-width: 1200px; }
.grid-table th, .grid-table td { border: 1px solid #EBEEF5; padding: 8px; font-size: 13px; }
.grid-table th { background-color: #f5f7fa; text-align: center; font-weight: bold; position: sticky; top: 0; z-index: 10; }
.title-cell { width: 250px; }
.group-row { background-color: #e8f5e9; }
.calculated-row { background-color: #c8e6c9; font-weight: bold; }
.readonly-text { text-align: right; padding-right: 10px !important; }
.add-category-section { margin-top: 20px; padding: 15px; border: 1px dashed #dcdfe6; background-color: #fafafa; border-radius: 4px; display: flex; align-items: center; }
::v-deep .el-input__inner { text-align: right; font-size: 13px; }
</style>
